import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Layout } from "./components/Layout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import VerifySignup from "./pages/VerifySignup";
import Dashboard from "./pages/Dashboard";
import Incidents from "./pages/Incidents";
import Chain from "./pages/Chain";
import Analytics from "./pages/Analytics";
import Tenants from "./pages/Tenants";
import FMEA from "./pages/FMEA";
import EightD from "./pages/EightD";
import LPA from "./pages/LPA";
import Settings from "./pages/Settings";
import Exec from "./pages/Exec";
import AuditTrail from "./pages/AuditTrail";
import Notifications from "./pages/Notifications";
import NewIncidentWizard from "./pages/NewIncidentWizard";
import AiIncidentChat from "./pages/AiIncidentChat";
import AdminUsers from "./pages/AdminUsers";
import AdminAnalytics from "./pages/AdminAnalytics";
import AdminAiUsage from "./pages/AdminAiUsage";
import AdminCustomerRelationships from "./pages/AdminCustomerRelationships";
import AdminCustomerMasters from "./pages/AdminCustomerMasters";
import AdminRecurringSeries from "./pages/AdminRecurringSeries";
import AdminBiPlans from "./pages/AdminBiPlans";
import AdminBiFxRates from "./pages/AdminBiFxRates";
import AdminContractRates from "./pages/AdminContractRates";
import AdminFrameOrders from "./pages/AdminFrameOrders";
import AdminMinMarginLanes from "./pages/AdminMinMarginLanes";
import AdminDockScheduling from "./pages/AdminDockScheduling";
import DockCalendar from "./pages/DockCalendar";
import DockBook from "./pages/DockBook";
import DockMyBookings from "./pages/DockMyBookings";
import DockGateLookup from "./pages/DockGateLookup";
import AdminDockSearch from "./pages/AdminDockSearch";
import AdminPlantGrants from "./pages/AdminPlantGrants";
import Approvals from "./pages/Approvals";
import BIDashboard from "./pages/BIDashboard";
import { PortalLayout } from "./components/PortalLayout";
import PortalMyRequests from "./pages/portal/PortalMyRequests";
import PortalNewRequest from "./pages/portal/PortalNewRequest";
import PortalChat from "./pages/portal/PortalChat";
import PortalTimeline from "./pages/portal/PortalTimeline";
import PortalClaims from "./pages/portal/PortalClaims";
import PortalApprovals from "./pages/portal/PortalApprovals";
import CarrierBidPage from "./pages/CarrierBid";
import OfferShare from "./pages/OfferShare";
import LogisticsRequests from "./pages/LogisticsRequests";
import LogisticsEditor from "./pages/LogisticsEditor";
import LogisticsDetail from "./pages/LogisticsDetail";
import LogisticsIntakeChat from "./pages/LogisticsIntakeChat";
import AdminIndex from "./pages/AdminIndex";
import AdminSlaRules from "./pages/AdminSlaRules";
import AdminEscalationChains from "./pages/AdminEscalationChains";
import AdminIncidentTemplates from "./pages/AdminIncidentTemplates";
import AdminSrTypes from "./pages/AdminSrTypes";
import AdminPendingApprovals from "./pages/AdminPendingApprovals";
import AdminTenantTypes from "./pages/AdminTenantTypes";
import AdminIncidentCategories from "./pages/AdminIncidentCategories";
import AdminLogisticsVehicleTypes from "./pages/AdminLogisticsVehicleTypes";
import AdminLogisticsApprovalRules from "./pages/AdminLogisticsApprovalRules";
import AdminLogisticsApprovalQueue from "./pages/AdminLogisticsApprovalQueue";
import AdminLogisticsCategories from "./pages/AdminLogisticsCategories";
import AdminTenants from "./pages/AdminTenants";
import Sorting from "./pages/Sorting";
import TMS from "./pages/TMS";
import Engineering from "./pages/Engineering";
import StockLocks from "./pages/StockLocks";
import MaterialMovements from "./pages/MaterialMovements";
import Countdown from "./pages/Countdown";
import TierSettings from "./pages/TierSettings";
import ServiceRequests from "./pages/ServiceRequests";
import { RouteGuard, ModuleGuard } from "./components/RoleGuard";

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/verify" element={<VerifySignup />} />

        {/* Phase C — public carrier bidder. NO auth — token is the gate. */}
        <Route path="/carrier/bid/:token" element={<CarrierBidPage />} />

        {/* Phase E — public offer share preview. NO auth — token is the gate. */}
        <Route path="/offer/:token" element={<OfferShare />} />

        {/* Phase B — Customer portal shell. Customer-tier users are auto-
            routed here by ProtectedRoute. */}
        <Route
          path="/portal"
          element={
            <ProtectedRoute>
              <PortalLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<PortalMyRequests />} />
          <Route path="my-requests" element={<PortalMyRequests />} />
          <Route path="new" element={<PortalNewRequest />} />
          <Route path="chat" element={<PortalChat />} />
          <Route path="r/:rid" element={<PortalTimeline />} />
          <Route path="claims" element={<PortalClaims />} />
          <Route path="approvals" element={<PortalApprovals />} />
        </Route>
        <Route
          path="/incidents/new"
          element={
            <ProtectedRoute>
              <RouteGuard minRole="responder">
                <NewIncidentWizard />
              </RouteGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/incidents/new/chat"
          element={
            <ProtectedRoute>
              <RouteGuard minRole="responder">
                <AiIncidentChat />
              </RouteGuard>
            </ProtectedRoute>
          }
        />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="incidents" element={<ModuleGuard module="incidents"><Incidents /></ModuleGuard>} />
          <Route path="chain" element={<ModuleGuard module="chain"><Chain /></ModuleGuard>} />
          <Route path="analytics" element={<ModuleGuard module="analytics"><Analytics /></ModuleGuard>} />
          <Route path="tenants" element={<ModuleGuard module="tenants"><Tenants /></ModuleGuard>} />
          <Route
            path="fmea"
            element={
              <ModuleGuard module="fmea">
                <RouteGuard minRole="responder">
                  <FMEA />
                </RouteGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="8d"
            element={
              <ModuleGuard module="8d">
                <RouteGuard minRole="responder">
                  <EightD />
                </RouteGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="lpa"
            element={
              <ModuleGuard module="lpa">
                <RouteGuard minRole="responder">
                  <LPA />
                </RouteGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="exec"
            element={
              <ModuleGuard module="exec">
                <RouteGuard minRole="escalation_manager">
                  <Exec />
                </RouteGuard>
              </ModuleGuard>
            }
          />
          <Route path="audit-trail" element={<AuditTrail />} />
          <Route path="notifications" element={<Notifications />} />
          <Route
            path="logistics-requests"
            element={
              <RouteGuard minRole="responder">
                <LogisticsRequests />
              </RouteGuard>
            }
          />
          <Route
            path="logistics-requests/new"
            element={
              <RouteGuard minRole="responder">
                <LogisticsEditor />
              </RouteGuard>
            }
          />
          <Route
            path="logistics-requests/intake-chat"
            element={
              <RouteGuard minRole="responder">
                <LogisticsIntakeChat />
              </RouteGuard>
            }
          />
          <Route
            path="logistics-requests/:id"
            element={
              <RouteGuard minRole="responder">
                <LogisticsDetail />
              </RouteGuard>
            }
          />
          <Route
            path="logistics-requests/:id/edit"
            element={
              <RouteGuard minRole="responder">
                <LogisticsEditor />
              </RouteGuard>
            }
          />
          <Route
            path="sorting"
            element={
              <ModuleGuard module="sorting">
                <RouteGuard minRole="responder">
                  <Sorting />
                </RouteGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="tms"
            element={
              <ModuleGuard module="tms">
                <RouteGuard minRole="responder">
                  <TMS />
                </RouteGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="engineering"
            element={
              <ModuleGuard module="engineering">
                <RouteGuard minRole="responder">
                  <Engineering />
                </RouteGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="stock-locks"
            element={
              <ModuleGuard module="stock_lock">
                <RouteGuard minRole="responder">
                  <StockLocks />
                </RouteGuard>
              </ModuleGuard>
            }
          />
          <Route
            path="material-movements"
            element={
              <RouteGuard minRole="responder">
                <MaterialMovements />
              </RouteGuard>
            }
          />
          <Route
            path="countdown"
            element={
              <RouteGuard minRole="responder">
                <Countdown />
              </RouteGuard>
            }
          />
          <Route
            path="settings/tier"
            element={
              <RouteGuard minRole="responder">
                <TierSettings />
              </RouteGuard>
            }
          />
          <Route
            path="service-requests"
            element={
              <RouteGuard minRole="responder">
                <ServiceRequests />
              </RouteGuard>
            }
          />
          <Route
            path="admin"
            element={
              <RouteGuard minRole="tenant_admin">
                <AdminIndex />
              </RouteGuard>
            }
          >
            <Route path="sla" element={<AdminSlaRules />} />
            <Route path="escalation" element={<AdminEscalationChains />} />
            <Route path="templates" element={<AdminIncidentTemplates />} />
            <Route path="sr-types" element={<AdminSrTypes />} />
            <Route path="tenant-types" element={<AdminTenantTypes />} />
            <Route path="tenants" element={<AdminTenants />} />
            <Route path="incident-categories" element={<AdminIncidentCategories />} />
            <Route path="approvals" element={<AdminPendingApprovals />} />
            <Route path="logistics-vehicle-types" element={<AdminLogisticsVehicleTypes />} />
            <Route path="logistics-approval-rules" element={<AdminLogisticsApprovalRules />} />
            <Route path="logistics-approval-queue" element={<AdminLogisticsApprovalQueue />} />
            <Route path="logistics-categories" element={<AdminLogisticsCategories />} />
          </Route>
          <Route
            path="admin/users"
            element={
              <RouteGuard minRole="tenant_admin">
                <AdminUsers />
              </RouteGuard>
            }
          />
          <Route
            path="admin/analytics"
            element={
              <RouteGuard minRole="platform_admin">
                <AdminAnalytics />
              </RouteGuard>
            }
          />
          <Route
            path="admin/ai-usage"
            element={
              <RouteGuard minRole="platform_admin">
                <AdminAiUsage />
              </RouteGuard>
            }
          />
          <Route
            path="admin/customer-relationships"
            element={
              <RouteGuard minRole="platform_admin">
                <AdminCustomerRelationships />
              </RouteGuard>
            }
          />
          <Route
            path="admin/customer-masters"
            element={
              <RouteGuard minRole="responder">
                <AdminCustomerMasters />
              </RouteGuard>
            }
          />
          <Route
            path="admin/recurring-series"
            element={
              <RouteGuard minRole="responder">
                <AdminRecurringSeries />
              </RouteGuard>
            }
          />
          <Route
            path="bi/dashboard"
            element={
              <RouteGuard minRole="responder">
                <BIDashboard />
              </RouteGuard>
            }
          />
          <Route
            path="admin/bi-plans"
            element={
              <RouteGuard minRole="responder">
                <AdminBiPlans />
              </RouteGuard>
            }
          />
          <Route
            path="admin/bi-fx-rates"
            element={
              <RouteGuard minRole="responder">
                <AdminBiFxRates />
              </RouteGuard>
            }
          />
          <Route
            path="admin/contract-rates"
            element={
              <RouteGuard minRole="responder">
                <AdminContractRates />
              </RouteGuard>
            }
          />
          <Route
            path="admin/frame-orders"
            element={
              <RouteGuard minRole="responder">
                <AdminFrameOrders />
              </RouteGuard>
            }
          />
          <Route
            path="admin/min-margin-lanes"
            element={
              <RouteGuard minRole="responder">
                <AdminMinMarginLanes />
              </RouteGuard>
            }
          />
          <Route
            path="admin/dock-scheduling"
            element={
              <RouteGuard minRole="responder">
                <AdminDockScheduling />
              </RouteGuard>
            }
          />
          <Route
            path="dock-scheduling/calendar"
            element={
              <RouteGuard minRole="responder">
                <DockCalendar />
              </RouteGuard>
            }
          />
          <Route
            path="dock-scheduling/book"
            element={
              <RouteGuard minRole="responder">
                <DockBook />
              </RouteGuard>
            }
          />
          <Route
            path="dock-scheduling/my-bookings"
            element={
              <RouteGuard minRole="responder">
                <DockMyBookings />
              </RouteGuard>
            }
          />
          <Route
            path="dock-scheduling/gate"
            element={
              <RouteGuard minRole="responder">
                <DockGateLookup />
              </RouteGuard>
            }
          />
          <Route
            path="dock-scheduling/search"
            element={
              <RouteGuard minRole="responder">
                <AdminDockSearch />
              </RouteGuard>
            }
          />
          <Route
            path="admin/plant-grants"
            element={
              <RouteGuard minRole="responder">
                <AdminPlantGrants />
              </RouteGuard>
            }
          />
          <Route
            path="approvals"
            element={
              <RouteGuard minRole="responder">
                <Approvals />
              </RouteGuard>
            }
          />
          <Route path="settings" element={<Settings />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
